import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Paper, 
  TextField, 
  Button, 
  Alert 
} from '@mui/material';

const SuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        const sessionId = searchParams.get('session_id');
        
        if (sessionId) {
          // First call the success endpoint to set up session
          const successResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/payments/success?session_id=${sessionId}`,
            { credentials: 'include' }
          );
          
          if (successResponse.ok) {
            const successData = await successResponse.json();
            if (successData.success) {
              if (successData.needs_setup) {
                setNeedsSetup(true);
                setLoading(false);
                return;
              } else {
                // User is fully set up, redirect to businesses
                setTimeout(() => {
                  navigate('/businesses');
                }, 2000);
                return;
              }
            }
          }
        }
        
        // Fallback: Check if user is authenticated and needs password setup
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const userData = await response.json();
          // If user has no password (empty string), they need to set it up
          if (!userData.password || userData.password === '') {
            setNeedsSetup(true);
            setLoading(false);
          } else {
            // User is fully set up, redirect to businesses
            setTimeout(() => {
              navigate('/businesses');
            }, 2000);
          }
        } else {
          // Not authenticated, redirect to home
          navigate('/');
        }
      } catch (error) {
        console.error('Error checking user status:', error);
        navigate('/');
      }
    };

    checkUserStatus();
  }, [navigate, searchParams]);

  const handlePasswordSetup = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setSetupLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/complete-setup`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        navigate('/businesses');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to set up account');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ mb: 4 }} />
        <Typography variant="h5" align="center" gutterBottom>
          Payment Successful!
        </Typography>
        <Typography variant="body1" align="center" color="textSecondary">
          Setting up your account...
        </Typography>
      </Box>
    );
  }

  if (needsSetup) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
        }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            borderRadius: '16px',
            boxShadow: '0 8px 32px -12px rgba(0,0,0,0.1)',
          }}
        >
          <Typography variant="h4" align="center" gutterBottom sx={{ color: '#1976d2', fontWeight: 700 }}>
            Welcome to Sashy!
          </Typography>
          <Typography variant="body1" align="center" sx={{ mb: 3, color: '#546E7A' }}>
            Your payment was successful. Complete your account setup to get started.
          </Typography>
          
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <form onSubmit={handlePasswordSetup}>
            <TextField
              fullWidth
              type="password"
              label="Set Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 3 }}
              required
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={setupLoading}
              sx={{
                py: 1.5,
                borderRadius: '50px',
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #1976d2 0%, #1565C0 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
                }
              }}
            >
              {setupLoading ? <CircularProgress size={20} color="inherit" /> : 'Complete Setup'}
            </Button>
          </form>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
      }}
    >
      <CircularProgress size={60} thickness={4} sx={{ mb: 4 }} />
      <Typography variant="h5" align="center" gutterBottom>
        Payment Successful!
      </Typography>
      <Typography variant="body1" align="center" color="textSecondary" sx={{ mb: 4 }}>
        Redirecting you to your businesses dashboard...
      </Typography>
    </Box>
  );
};

export default SuccessPage;
